import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Gem, 
  ChevronLeft, 
  Heart, 
  Droplets,
  ExternalLink,
  Tag,
  Sparkles,
  ShoppingBag,
  Package
} from "lucide-react";
import { LithaeLogo, LithaeIcon } from "@/components/LithaeLogo";
import { getStoneById } from "@/data/stones";
import { getProductSuggestions, PRODUCT_TYPES } from "@/data/products";

// Product Suggestions Card Component
const ProductSuggestionsCard = ({ productSuggestions, userUsage, setUserUsage, compact = false, subtle = false }) => {
  if (!productSuggestions || productSuggestions.products.length === 0) {
    return null;
  }

  // Subtle version - less imposing, simpler design
  if (subtle) {
    return (
      <div className="border-t border-border pt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Voir aussi nos produits {productSuggestions.stoneName}
            </h3>
          </div>
          <Button 
            variant="ghost"
            size="sm"
            onClick={() => window.open(productSuggestions.shopUrl, '_blank')}
            className="text-primary hover:text-primary/80"
          >
            Voir tout
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
        
        {/* Compact horizontal product list */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {productSuggestions.products.slice(0, 4).map((product, index) => (
            <a
              key={index}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex-shrink-0 w-32 hover:opacity-80 transition-opacity"
            >
              <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-2">
                <img 
                  src={product.imageUrl} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 group-hover:text-foreground transition-colors">
                {product.name}
              </p>
            </a>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-soft border-primary/20 bg-gradient-to-br from-card to-primary/5">
      <CardContent className={compact ? "p-4 sm:p-6" : "p-6 sm:p-8"}>
        <div className="flex items-center gap-3 mb-2">
          <div className={`${compact ? 'w-8 h-8' : 'w-10 h-10'} bg-primary/20 rounded-xl flex items-center justify-center`}>
            <ShoppingBag className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-primary`} />
          </div>
          <div>
            <h2 className={`font-serif ${compact ? 'text-lg' : 'text-xl sm:text-2xl'} font-semibold text-foreground`}>
              Découvrez nos {productSuggestions.stoneName}
            </h2>
          </div>
        </div>
        <p className={`text-muted-foreground ${compact ? 'mb-4 ml-11 text-sm' : 'mb-6 ml-[52px]'}`}>
          {productSuggestions.usageDescription}
        </p>
        
        {/* Usage selector */}
        <div className={compact ? "mb-4" : "mb-6"}>
          <p className="text-sm text-muted-foreground mb-3">Votre utilisation :</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(PRODUCT_TYPES).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setUserUsage(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  userUsage === key 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {value.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products grid */}
        <div className={`grid ${compact ? 'grid-cols-2 sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-3'} gap-4`}>
          {productSuggestions.products.slice(0, compact ? 3 : productSuggestions.products.length).map((product, index) => (
            <a
              key={index}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-card rounded-xl border border-border overflow-hidden hover:shadow-medium hover:-translate-y-1 transition-all duration-300"
            >
              <div className={`${compact ? 'aspect-[4/3]' : 'aspect-square'} bg-muted relative overflow-hidden`}>
                <img 
                  src={product.imageUrl} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-full items-center justify-center absolute inset-0 bg-muted">
                  <Package className="w-12 h-12 text-muted-foreground/50" />
                </div>
              </div>
              <div className={compact ? "p-3" : "p-4"}>
                <Badge variant="secondary" className="mb-2 text-xs">
                  {product.type}
                </Badge>
                <h3 className={`font-medium text-foreground group-hover:text-primary transition-colors ${compact ? 'text-sm line-clamp-1' : 'line-clamp-2'}`}>
                  {product.name}
                </h3>
                <p className="text-sm text-primary mt-2 flex items-center gap-1">
                  Voir le produit
                  <ExternalLink className="w-3 h-3" />
                </p>
              </div>
            </a>
          ))}
        </div>

        {/* View all products button */}
        <div className={`${compact ? 'mt-4' : 'mt-6'} text-center`}>
          <Button 
            variant="primary"
            size={compact ? "default" : "default"}
            onClick={() => window.open(productSuggestions.shopUrl, '_blank')}
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Voir tous les produits {productSuggestions.stoneName}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Stone color mapping for backgrounds
const stoneColorMap = {
  'amethyste': 'bg-stone-amethyst',
  'quartz-rose': 'bg-stone-quartz',
  'labradorite': 'bg-stone-lapis/50',
  'obsidienne': 'bg-stone-obsidian/10',
  'citrine': 'bg-stone-tiger',
  'lapis-lazuli': 'bg-stone-lapis',
  'oeil-de-tigre': 'bg-stone-tiger',
  'howlite': 'bg-muted'
};

// Chakra color mapping
const chakraColorMap = {
  'Racine': 'bg-chakra-root text-white',
  'Sacré': 'bg-chakra-sacral text-white',
  'Plexus Solaire': 'bg-chakra-plexus text-foreground',
  'Cœur': 'bg-chakra-heart text-white',
  'Gorge': 'bg-chakra-throat text-white',
  'Troisième Œil': 'bg-chakra-third-eye text-white',
  'Couronne': 'bg-chakra-crown text-white'
};

const Stone = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const stone = getStoneById(id);
  
  // Get user profile from localStorage to determine usage preference
  const [userUsage, setUserUsage] = useState('sur_soi');
  const [productSuggestions, setProductSuggestions] = useState(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    // Load user profile from localStorage
    const savedProfile = localStorage.getItem('quiz_profile');
    if (savedProfile) {
      const profile = JSON.parse(savedProfile);
      if (profile.usage) {
        setUserUsage(profile.usage);
      }
    }
  }, []);

  useEffect(() => {
    // Get product suggestions based on stone and usage
    if (id && stone) {
      const suggestions = getProductSuggestions(id, userUsage, stone);
      setProductSuggestions(suggestions);
    }
  }, [id, userUsage, stone]);

  if (!stone) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <LithaeIcon size={64} className="mx-auto mb-6 text-muted-foreground" />
          <h1 className="font-serif text-2xl font-semibold text-foreground mb-4">
            Pierre non trouvée
          </h1>
          <p className="text-muted-foreground mb-8">
            Cette pierre n&apos;existe pas dans notre base de données.
          </p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Retour à l&apos;accueil
          </Button>
        </div>
      </div>
    );
  }

  const bgColor = stoneColorMap[stone.id] || 'bg-muted';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-md border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Retour</span>
            </button>
            <div className="flex items-center gap-2">
              <LithaeIcon size={20} />
              <span className="font-serif text-lg font-medium text-foreground">
                Fiche pierre
              </span>
            </div>
            <div className="w-16" /> {/* Spacer */}
          </div>
        </div>
      </header>

      {/* Hero section with stone image */}
      <section className={`${bgColor} relative`}>
        {/* Stone Image */}
        <div className="max-w-4xl mx-auto">
          <div className="aspect-[16/9] sm:aspect-[21/9] relative overflow-hidden">
            {stone.imageUrl ? (
              <img 
                src={stone.imageUrl} 
                alt={stone.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center py-16">
                <Gem className="w-24 h-24 text-primary/30" />
              </div>
            )}
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        </div>
        {/* Stone info overlay */}
        <div className="absolute bottom-0 left-0 right-0 pb-8 pt-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-foreground mb-3">
              {stone.name}
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              {stone.couleur}
            </p>
          
            {/* Keywords */}
            <div className="flex flex-wrap justify-center gap-2">
              {stone.keywords.map((keyword, index) => (
                <Badge key={index} variant="secondary" className="bg-card/80 text-foreground">
                  {keyword}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product Suggestions - Top (compact version) */}
      {productSuggestions && productSuggestions.products.length > 0 && (
        <section className="py-8 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <ProductSuggestionsCard 
              productSuggestions={productSuggestions}
              userUsage={userUsage}
              setUserUsage={setUserUsage}
              compact={true}
            />
          </div>
        </section>
      )}

      {/* Main content */}
      <main className="py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Bienfaits émotionnels & spirituels */}
          <Card className="shadow-soft">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Heart className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                  Bienfaits émotionnels & spirituels
                </h2>
              </div>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {stone.bienfaits_emo_spirituels}
              </p>
            </CardContent>
          </Card>

          {/* Entretien */}
          <Card className="shadow-soft">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                  Entretien & Purification
                </h2>
              </div>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {stone.entretien}
              </p>
            </CardContent>
          </Card>

          {/* Chakras */}
          <Card className="shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-foreground">
                  Chakras associés
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {stone.chakras.map((chakra, index) => (
                  <Badge 
                    key={index} 
                    className={`${chakraColorMap[chakra] || 'bg-muted text-foreground'} px-4 py-2 text-sm`}
                  >
                    {chakra}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Propriétés */}
          <Card className="shadow-soft">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Tag className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                  Propriétés
                </h2>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Intensité énergétique
                  </h3>
                  <Badge 
                    variant="secondary" 
                    className={`
                      ${stone.intensity === 'douce' ? 'bg-secondary' : ''}
                      ${stone.intensity === 'moyenne' ? 'bg-primary/20 text-primary' : ''}
                      ${stone.intensity === 'forte' ? 'bg-destructive/20 text-destructive' : ''}
                      px-4 py-2
                    `}
                  >
                    {stone.intensity === 'douce' && '🌸 Douce'}
                    {stone.intensity === 'moyenne' && '⚡ Moyenne'}
                    {stone.intensity === 'forte' && '🔥 Forte'}
                  </Badge>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Style énergétique
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {stone.energyStyle.map((style, index) => (
                      <Badge key={index} variant="outline">
                        {style}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Besoins adressés
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {stone.needs.map((need, index) => (
                      <Badge key={index} variant="secondary">
                        {need.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Utilisations recommandées
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {stone.usageFits.map((usage, index) => (
                      <Badge key={index} variant="outline">
                        {usage.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Suggestions - Bottom (subtle version) */}
          {productSuggestions && productSuggestions.products.length > 0 && (
            <ProductSuggestionsCard 
              productSuggestions={productSuggestions}
              userUsage={userUsage}
              setUserUsage={setUserUsage}
              subtle={true}
            />
          )}

          {/* Source button */}
          <div className="text-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.open(stone.sourceUrl, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Voir la fiche complète sur Lithae
            </Button>
          </div>
        </div>
      </main>

      {/* Disclaimer */}
      <section className="py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs text-muted-foreground italic">
            Approche de bien-être, ne remplace pas un avis médical. 
            Les informations fournies sont à titre indicatif et ne constituent pas un conseil médical.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <a href="https://www.lithae.com" target="_blank" rel="noopener noreferrer">
              <LithaeLogo size="small" />
            </a>
            <Button variant="ghost" onClick={() => navigate('/quiz')}>
              Refaire le quiz
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Stone;
